import React, { useMemo } from 'react';

function AnimatedHeroBackgroundInner() {
  // Memoize particles so they're computed once, not on every parent re-render.
  // This prevents layout thrashing from repeatedly creating elements with
  // randomized inline styles.
  const particles = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${3 + Math.random() * 4}s`,
      })),
    []
  );

  const gridCells = useMemo(
    () =>
      Array.from({ length: 144 }).map((_, i) => ({
        id: i,
        animationDelay: `${i * 0.1}s`,
      })),
    []
  );

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      // CSS containment isolates layout/paint work inside this subtree,
      // preventing animations here from forcing reflows on the rest of the page.
      style={{ contain: 'layout paint style', willChange: 'transform' }}
      aria-hidden="true"
    >
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-primary/5" />
      
      {/* Animated Orbs */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute top-3/4 right-1/4 w-48 h-48 bg-secondary/20 rounded-full blur-3xl animate-pulse delay-1000" />
      <div className="absolute bottom-1/4 left-1/3 w-32 h-32 bg-accent/20 rounded-full blur-2xl animate-pulse delay-500" />
      
      {/* Floating Particles */}
      <div className="absolute inset-0">
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute w-2 h-2 bg-primary/30 rounded-full animate-float"
            style={{
              left: p.left,
              top: p.top,
              animationDelay: p.animationDelay,
              animationDuration: p.animationDuration,
            }}
          />
        ))}
      </div>
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="grid grid-cols-12 gap-4 h-full">
          {gridCells.map((c) => (
            <div
              key={c.id}
              className="border border-primary/20 rounded"
              style={{
                animationDelay: c.animationDelay,
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Moving Lines */}
      <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
            <stop offset="50%" stopColor="currentColor" stopOpacity="1" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <g className="text-primary">
          <line x1="0" y1="20%" x2="100%" y2="80%" stroke="url(#lineGradient)" strokeWidth="2" className="animate-draw" />
          <line x1="20%" y1="0" x2="80%" y2="100%" stroke="url(#lineGradient)" strokeWidth="2" className="animate-draw delay-1000" />
          <line x1="100%" y1="30%" x2="0%" y2="70%" stroke="url(#lineGradient)" strokeWidth="2" className="animate-draw delay-2000" />
        </g>
      </svg>
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
    </div>
  );
}

// Memoize to prevent re-rendering when parent state changes (which was
// triggering layout thrashing across the animated subtree).
export const AnimatedHeroBackground = React.memo(AnimatedHeroBackgroundInner);
